import { useEffect, useRef, useState } from 'react';
import { useBarcodeScanner } from '@hooks/useBarcodeScanner';
import { readNumbersFromImage } from '@lib/ocr';

/**
 * Identify a member three ways, all feeding the same lookup: type it, scan the barcode with the live
 * camera, or - when there is no barcode - tap "Read numbers from photo" to OCR the printed digits off
 * a still photo (Tesseract.js, on-device). OCR only suggests candidate numbers; the user taps one to
 * look it up and confirms the member.
 */
export function ScanInput({ onToken, busy }: { onToken: (token: string) => void; busy?: boolean }) {
  const [value, setValue] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scanner = useBarcodeScanner();

  const ocrInputRef = useRef<HTMLInputElement | null>(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrCandidates, setOcrCandidates] = useState<string[]>([]);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [ocrRan, setOcrRan] = useState(false);

  useEffect(() => {
    if (cameraOn && videoRef.current) {
      void scanner.start(videoRef.current, (text) => {
        setCameraOn(false);
        onToken(text);
      });
      return () => scanner.stop();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOn]);

  const submit = () => {
    const t = value.trim();
    if (t) {
      onToken(t);
      setValue('');
    }
  };

  const runOcr = async (file: File) => {
    setOcrBusy(true);
    setOcrError(null);
    setOcrCandidates([]);
    setOcrRan(true);
    setOcrProgress(0);
    try {
      const nums = await readNumbersFromImage(file, setOcrProgress);
      setOcrCandidates(nums);
    } catch {
      setOcrError('Could not read the photo. Try again, or type the number.');
    } finally {
      setOcrBusy(false);
    }
  };

  return (
    <div className="card">
      <label className="field-label" htmlFor="scan-token">
        Scan or enter a kit, accession, USPS tracking, member ID, or name
      </label>
      <div className="flex gap-2">
        <input
          id="scan-token"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          className="text-input min-w-0 flex-1 py-3 text-base"
          placeholder="e.g. 39016812"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !value.trim()}
          className="btn-bench shrink-0 px-5 disabled:opacity-50"
        >
          {busy ? '...' : 'Look up'}
        </button>
      </div>

      {scanner.supported && (
        <button
          type="button"
          onClick={() => setCameraOn((v) => !v)}
          className="btn-bench btn-bench-ghost mt-3 w-full"
        >
          {cameraOn ? 'Stop camera' : 'Scan with camera'}
        </button>
      )}
      {cameraOn && (
        <div className="mt-3 overflow-hidden rounded-cdx" style={{ background: '#000' }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} className="w-full" muted playsInline />
        </div>
      )}
      {scanner.error && <p className="mt-2 text-xs text-cdx-danger">{scanner.error}</p>}

      {/* No-barcode fallback: OCR the printed numbers from a photo (on-device). The input accepts a
          fresh camera capture OR an existing gallery still (no `capture` attribute), so staff can pick
          a photo already on the shared bench phone. */}
      <input
        ref={ocrInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void runOcr(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => ocrInputRef.current?.click()}
        disabled={busy || ocrBusy}
        className="btn-bench btn-bench-ghost mt-2 w-full disabled:opacity-50"
      >
        {ocrBusy ? `Reading photo... ${ocrProgress}%` : 'Read numbers from photo (no barcode)'}
      </button>

      {ocrCandidates.length > 0 && (
        <div className="mt-3">
          <p className="mb-2 text-xs" style={{ color: 'var(--ink-400)' }}>
            Numbers found - tap the right one to look it up:
          </p>
          <div className="flex flex-wrap gap-2">
            {ocrCandidates.map((n) => (
              <button
                key={n}
                type="button"
                disabled={busy}
                onClick={() => {
                  setOcrCandidates([]);
                  setOcrRan(false);
                  onToken(n);
                }}
                className="chip disabled:opacity-50"
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}
      {ocrRan && !ocrBusy && ocrCandidates.length === 0 && !ocrError && (
        <p className="mt-2 text-xs" style={{ color: 'var(--amber)' }}>
          No numbers detected in that photo. Try a sharper shot, or type it above.
        </p>
      )}
      {ocrError && <p className="mt-2 text-xs text-cdx-danger">{ocrError}</p>}
    </div>
  );
}
